import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    triggerDriftReport,
    getDriftReportStatus,
    getDriftReportHistory,
    browseDriftReport,
    getDriftReportPresignedUrl,
    getDriftReportAvailableMonths,
    downloadDriftReportZip,
    deleteDriftReport,
} from '../services/api';
import '../styles/DriftReportManagement.css';

const POLL_INTERVAL_MS = 10000;
const PAGE_SIZE = 10;

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// Friendly labels for the drift-report category folders produced by the framework.
const CATEGORY_LABELS = {
    data_drift: 'Data Drift',
    feature_drift_report: 'Feature Drift',
    prediction_drift_report: 'Prediction Drift',
    explainability_drift_report: 'Explainability Drift',
    CSI_report: 'CSI Report',
    PSI_report: 'PSI Report',
    '(root)': 'Other',
};
const categoryLabel = c => CATEGORY_LABELS[c] || c;

const StatusBadge = ({ status }) => {
    const cls = {
        SUCCESS:   'drf-badge drf-badge-success',
        FAIL:      'drf-badge drf-badge-fail',
        RUNNING:   'drf-badge drf-badge-running',
        SUBMITTED: 'drf-badge drf-badge-submitted',
    };
    return <span className={cls[status] || 'drf-badge drf-badge-submitted'}>{status}</span>;
};

const DriftFileExplorer = ({ uid, token, onClose }) => {
    const [tree, setTree]               = useState(null);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [openModel, setOpenModel]     = useState(null);
    const [openVersion, setOpenVersion] = useState({});
    const [openCategory, setOpenCategory] = useState({});
    const [opening, setOpening]         = useState(null);
    const [dlKey, setDlKey]             = useState(null);   // active download scope key

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        browseDriftReport(token, uid)
            .then(res  => {
                if (cancelled) return;
                // Tag every file with the path it should occupy inside a downloaded
                // zip, mirroring the S3 layout: {model}/{version}/{category}/{file}
                const data = res.data;
                (data.models || []).forEach(m =>
                    (m.versions || []).forEach(v =>
                        (v.categories || []).forEach(c =>
                            (c.files || []).forEach(f => {
                                f.arcname = `${m.model_name}/${v.version}/${c.category}/${f.name}`;
                            })
                        )
                    )
                );
                setTree(data);
            })
            .catch(err => { if (!cancelled) setError(err.response?.data?.message || err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [uid, token]);

    const handleOpenFile = async (s3Path) => {
        setOpening(s3Path);
        try {
            const res = await getDriftReportPresignedUrl(token, s3Path);
            window.open(res.data.presigned_url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            alert(`Could not open file: ${err.response?.data?.message || err.message}`);
        } finally {
            setOpening(null);
        }
    };

    const handleDownload = async (files, key, zipName) => {
        if (!files.length) return;
        setDlKey(key);
        try {
            // Server fetches the files from S3 and streams back a single zip,
            // avoiding browser CORS restrictions on the S3 presigned URLs.
            const zipFiles = files.map(f => ({ s3_path: f.s3_path, arcname: f.arcname }));
            const res = await downloadDriftReportZip(token, zipFiles, zipName || 'drift_reports');
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `${zipName || 'drift_reports'}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert(`Could not download reports: ${err.response?.data?.message || err.message}`);
        } finally {
            setDlKey(null);
        }
    };

    const dlLabel = (key, count) => {
        if (dlKey === key) return '⬇ …';
        return `⬇ ${count}`;
    };

    const toggleVersion = (modelName, version) => {
        setOpenVersion(prev => ({
            ...prev,
            [`${modelName}__${version}`]: !prev[`${modelName}__${version}`],
        }));
    };

    const toggleCategory = (catKey) => {
        setOpenCategory(prev => ({ ...prev, [catKey]: !prev[catKey] }));
    };

    // Files for a version live one level deeper now: version -> categories -> files.
    const versionFiles = v => v.categories.flatMap(c => c.files);

    const totalFiles = tree?.models.reduce(
        (sum, m) => sum + m.versions.reduce((s, v) => s + versionFiles(v).length, 0), 0
    ) ?? 0;

    return (
        <div className="drf-explorer-overlay">
            <div className="drf-explorer-panel">
                <div className="drf-explorer-header">
                    <div>
                        <h3 className="drf-explorer-title">📂 Drift Reports</h3>
                        {tree && (
                            <p className="drf-explorer-meta">
                                {tree.mode} &nbsp;·&nbsp; {tree.start_date} → {tree.end_date}
                            </p>
                        )}
                    </div>
                    <div className="drf-explorer-actions">
                        {tree && totalFiles > 0 && (
                            <button
                                className="drf-download-all-btn"
                                onClick={() => handleDownload(
                                    tree.models.flatMap(m => m.versions.flatMap(v => versionFiles(v))),
                                    'global',
                                    `drift_reports_${tree.mode}_${tree.start_date}_${tree.end_date}`
                                )}
                                disabled={dlKey !== null}
                                title="Download all report files"
                            >
                                {dlLabel('global', `Download All (${totalFiles})`)}
                            </button>
                        )}
                        <button className="drf-explorer-close" onClick={onClose} title="Close">✕</button>
                    </div>
                </div>

                <div className="drf-explorer-body">
                    {loading && (
                        <div className="drf-explorer-loading">
                            <span className="drf-btn-spinner drf-spinner-dark"></span>
                            Loading report structure…
                        </div>
                    )}
                    {error && <div className="drf-explorer-error">⚠️ {error}</div>}
                    {tree && !loading && (
                        tree.models.length === 0 ? (
                            <div className="drf-explorer-empty">No HTML report files found under this prefix.</div>
                        ) : (
                            <div className="drf-model-list">
                                {tree.models.map(m => {
                                    const modelKey = `model__${m.model_name}`;
                                    const modelFiles = m.versions.flatMap(v => versionFiles(v));
                                    return (
                                        <div key={m.model_name} className="drf-model-node">
                                            <div className={`drf-model-row ${openModel === m.model_name ? 'open' : ''}`}>
                                                <button
                                                    className="drf-row-toggle"
                                                    onClick={() => setOpenModel(openModel === m.model_name ? null : m.model_name)}
                                                >
                                                    <span className="drf-folder-icon">📁</span>
                                                    <span className="drf-model-name">{m.model_name}</span>
                                                    <span className="drf-version-count">
                                                        {m.versions.length} version{m.versions.length !== 1 ? 's' : ''}
                                                    </span>
                                                    <span className="drf-chevron">{openModel === m.model_name ? '▾' : '▸'}</span>
                                                </button>
                                                <button
                                                    className="drf-row-dl-btn"
                                                    onClick={() => handleDownload(modelFiles, modelKey, `drift_${m.model_name}`)}
                                                    disabled={dlKey !== null}
                                                    title={`Download all ${modelFiles.length} files for ${m.model_name}`}
                                                >
                                                    {dlLabel(modelKey, modelFiles.length)}
                                                </button>
                                            </div>

                                            {openModel === m.model_name && (
                                                <div className="drf-version-list">
                                                    {m.versions.map(v => {
                                                        const vKey = `${m.model_name}__${v.version}`;
                                                        const isOpen = !!openVersion[vKey];
                                                        const vFiles = versionFiles(v);
                                                        return (
                                                            <div key={v.version} className="drf-version-node">
                                                                <div className={`drf-version-row ${isOpen ? 'open' : ''}`}>
                                                                    <button
                                                                        className="drf-row-toggle"
                                                                        onClick={() => toggleVersion(m.model_name, v.version)}
                                                                    >
                                                                        <span className="drf-folder-icon">📂</span>
                                                                        <span className="drf-version-name">v{v.version}</span>
                                                                        <span className="drf-file-count">
                                                                            {v.categories.length} report{v.categories.length !== 1 ? 's' : ''}
                                                                        </span>
                                                                        <span className="drf-chevron">{isOpen ? '▾' : '▸'}</span>
                                                                    </button>
                                                                    <button
                                                                        className="drf-row-dl-btn"
                                                                        onClick={() => handleDownload(vFiles, vKey, `drift_${m.model_name}_v${v.version}`)}
                                                                        disabled={dlKey !== null}
                                                                        title={`Download all ${vFiles.length} files for v${v.version}`}
                                                                    >
                                                                        {dlLabel(vKey, vFiles.length)}
                                                                    </button>
                                                                </div>

                                                                {isOpen && (
                                                                    <div className="drf-category-list">
                                                                        {v.categories.map(c => {
                                                                            const cKey = `${vKey}__${c.category}`;
                                                                            const cOpen = !!openCategory[cKey];
                                                                            return (
                                                                                <div key={c.category} className="drf-category-node">
                                                                                    <div className={`drf-category-row ${cOpen ? 'open' : ''}`}>
                                                                                        <button
                                                                                            className="drf-row-toggle"
                                                                                            onClick={() => toggleCategory(cKey)}
                                                                                        >
                                                                                            <span className="drf-folder-icon">🗂</span>
                                                                                            <span className="drf-category-name">{categoryLabel(c.category)}</span>
                                                                                            <span className="drf-file-count">
                                                                                                {c.files.length} file{c.files.length !== 1 ? 's' : ''}
                                                                                            </span>
                                                                                            <span className="drf-chevron">{cOpen ? '▾' : '▸'}</span>
                                                                                        </button>
                                                                                        <button
                                                                                            className="drf-row-dl-btn"
                                                                                            onClick={() => handleDownload(c.files, cKey, `drift_${m.model_name}_v${v.version}_${c.category}`)}
                                                                                            disabled={dlKey !== null}
                                                                                            title={`Download all ${c.files.length} files for ${categoryLabel(c.category)}`}
                                                                                        >
                                                                                            {dlLabel(cKey, c.files.length)}
                                                                                        </button>
                                                                                    </div>

                                                                                    {cOpen && (
                                                                                        <div className="drf-file-list">
                                                                                            {c.files.map(f => (
                                                                                                <div key={f.s3_path} className="drf-file-row">
                                                                                                    <span className="drf-file-icon">📄</span>
                                                                                                    <span className="drf-file-name">{f.name}</span>
                                                                                                    <button
                                                                                                        className="drf-view-btn"
                                                                                                        disabled={opening === f.s3_path}
                                                                                                        onClick={() => handleOpenFile(f.s3_path)}
                                                                                                    >
                                                                                                        {opening === f.s3_path
                                                                                                            ? <><span className="drf-btn-spinner"></span> Opening…</>
                                                                                                            : '↗ View'}
                                                                                                    </button>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

const HistoryTable = ({ token, refreshTick, onBrowse }) => {
    const [rows, setRows]             = useState([]);
    const [total, setTotal]           = useState(0);
    const [page, setPage]             = useState(1);
    const [loading, setLoading]       = useState(false);
    const [modeFilter, setModeFilter] = useState('');
    const [deletingUid, setDeletingUid] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const params = { page, page_size: PAGE_SIZE };
        if (modeFilter) params.mode = modeFilter;
        getDriftReportHistory(token, params)
            .then(res => {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [token, page, modeFilter]);

    useEffect(() => { load(); }, [load, refreshTick]);

    const handleDelete = async (row) => {
        const confirmed = window.confirm(
            `Delete this drift report run (${row.mode}: ${row.start_date} → ${row.end_date})?\n\n` +
            'This permanently removes the run and all its report files from S3. This cannot be undone.'
        );
        if (!confirmed) return;
        setDeletingUid(row.uid);
        try {
            await deleteDriftReport(token, row.uid);
            // If the last row on a page was deleted, step back a page.
            if (rows.length === 1 && page > 1) {
                setPage(p => p - 1);
            } else {
                load();
            }
        } catch (err) {
            alert(`Could not delete run: ${err.response?.data?.message || err.message}`);
        } finally {
            setDeletingUid(null);
        }
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const fmtDate = iso => iso ? iso.replace('T', ' ').slice(0, 16) : '—';

    return (
        <div className="drf-card">
            <div className="drf-card-title">Run History</div>

            <div className="drf-history-filters">
                <select
                    className="drf-select"
                    value={modeFilter}
                    onChange={e => { setModeFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All modes</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom</option>
                </select>
            </div>

            {loading ? (
                <div className="drf-table-loading">
                    <span className="drf-btn-spinner drf-spinner-dark"></span> Loading…
                </div>
            ) : rows.length === 0 ? (
                <div className="drf-table-empty">No drift report runs found.</div>
            ) : (
                <>
                    <div className="drf-table-wrap">
                        <table className="drf-table">
                            <thead>
                                <tr>
                                    <th>Mode</th>
                                    <th>Date Range</th>
                                    <th>Status</th>
                                    <th>Submitted</th>
                                    <th>Completed</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.uid}>
                                        <td><span className="drf-badge drf-badge-mode">{row.mode}</span></td>
                                        <td className="drf-date-cell">{row.start_date} → {row.end_date}</td>
                                        <td><StatusBadge status={row.status} /></td>
                                        <td className="drf-ts-cell">{fmtDate(row.submitted_at)}</td>
                                        <td className="drf-ts-cell">{fmtDate(row.completed_at)}</td>
                                        <td>
                                            <div className="drf-action-cell">
                                                {row.status === 'SUCCESS' && (
                                                    <button className="drf-browse-btn" onClick={() => onBrowse(row.uid)}>
                                                        📂 Browse
                                                    </button>
                                                )}
                                                {row.status === 'FAIL' && (
                                                    <span className="drf-fail-hint" title={row.error_message || ''}>⚠️ Failed</span>
                                                )}
                                                {(row.status === 'SUBMITTED' || row.status === 'RUNNING') && (
                                                    <span className="drf-inprogress-hint">⏳ In progress</span>
                                                )}
                                                {row.status !== 'SUBMITTED' && row.status !== 'RUNNING' && (
                                                    <button
                                                        className="drf-delete-btn"
                                                        onClick={() => handleDelete(row)}
                                                        disabled={deletingUid === row.uid}
                                                        title="Delete this run and its S3 data"
                                                    >
                                                        {deletingUid === row.uid
                                                            ? <span className="drf-btn-spinner"></span>
                                                            : '🗑 Delete'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="drf-pagination">
                            <button className="drf-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                ‹ Prev
                            </button>
                            <span className="drf-page-info">Page {page} of {totalPages}</span>
                            <button className="drf-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                Next ›
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const DriftReportManagement = () => {
    const [mode, setMode]           = useState('monthly');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate]     = useState('');
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const [activeUid, setActiveUid] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [browseUid, setBrowseUid] = useState(null);

    const [availableMonths, setAvailableMonths]   = useState(null);
    const [monthsLoading, setMonthsLoading]       = useState(false);
    const [selectedYear, setSelectedYear]         = useState('');
    const [selectedMonth, setSelectedMonth]       = useState('');

    const pollRef = useRef(null);
    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        if (mode !== 'monthly') return;
        let cancelled = false;
        setMonthsLoading(true);
        setAvailableMonths(null);
        getDriftReportAvailableMonths(token)
            .then(res => { if (!cancelled) setAvailableMonths(res.data); })
            .catch(() => { if (!cancelled) setAvailableMonths({ years: [] }); })
            .finally(() => { if (!cancelled) setMonthsLoading(false); });
        return () => { cancelled = true; };
    }, [mode, token]);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const startPolling = useCallback((uid) => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await getDriftReportStatus(token, uid);
                const data = res.data;
                setJobStatus(data);
                if (data.status === 'SUCCESS' || data.status === 'FAIL') {
                    stopPolling();
                    if (data.status === 'SUCCESS') setRefreshTick(t => t + 1);
                }
            } catch {
                // swallow transient errors; keep polling
            }
        }, POLL_INTERVAL_MS);
    }, [token, stopPolling]);

    useEffect(() => () => stopPolling(), [stopPolling]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setJobStatus(null);
        setActiveUid(null);
        stopPolling();

        const params = { mode };
        if (mode === 'monthly') {
            if (!selectedYear || !selectedMonth) {
                setError('Please select a year and month.');
                setLoading(false);
                return;
            }
            params.year = selectedYear;
            params.month = selectedMonth;
        } else if (mode === 'custom') {
            if (!startDate || !endDate) {
                setError('Start date and end date are required for custom mode.');
                setLoading(false);
                return;
            }
            if (endDate < startDate) {
                setError('End date must be on or after the start date.');
                setLoading(false);
                return;
            }
            params.start_date = startDate;
            params.end_date = endDate;
        }

        try {
            const { data } = await triggerDriftReport(token, params);
            setActiveUid(data.uid);
            setJobStatus(data);
            if (data.status === 'SUCCESS') {
                setRefreshTick(t => t + 1);
            } else if (data.status !== 'FAIL') {
                startPolling(data.uid);
            }
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data?.message || err.message;
            setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        stopPolling();
        setError(null);
        setJobStatus(null);
        setActiveUid(null);
        setStartDate('');
        setEndDate('');
        setSelectedYear('');
        setSelectedMonth('');
        setMode('monthly');
    };

    const renderStatusBanner = () => {
        if (!jobStatus) return null;
        const { status } = jobStatus;

        if (status === 'SUBMITTED' || status === 'RUNNING') {
            return (
                <div className="drf-status-card drf-status-loading">
                    <div className="drf-pulse-dot"></div>
                    <div>
                        <strong>{status === 'SUBMITTED' ? 'Job submitted' : 'Running on ECS Fargate'}</strong>
                        <span className="drf-poll-hint"> — checking every 10 s (uid: {activeUid})</span>
                    </div>
                </div>
            );
        }

        if (status === 'SUCCESS') {
            return (
                <div className="drf-status-card drf-status-success">
                    <div className="drf-status-header">
                        <span className="drf-status-icon">✅</span>
                        <div>
                            <h3 className="drf-status-title">Drift Report Ready</h3>
                            <p className="drf-status-sub">
                                Mode: <strong>{jobStatus.mode}</strong> &nbsp;·&nbsp;
                                {jobStatus.start_date} → {jobStatus.end_date}
                            </p>
                        </div>
                    </div>
                    <button className="drf-browse-btn drf-browse-btn-lg" onClick={() => setBrowseUid(activeUid)}>
                        📂 Browse Report Files
                    </button>
                </div>
            );
        }

        if (status === 'FAIL') {
            return (
                <div className="drf-status-card drf-status-error">
                    <div className="drf-status-header">
                        <span className="drf-status-icon">❌</span>
                        <div>
                            <h3 className="drf-status-title">Drift Report Failed</h3>
                            <p className="drf-status-sub">{jobStatus.message}</p>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="drf-wrapper">
            <div className="drf-header">
                <h2>📈 Drift Report Management</h2>
                <p>
                    Trigger feature drift analysis for model predictions. Reports are generated
                    asynchronously in ECS Fargate and stored in S3.
                </p>
            </div>

            <div className="drf-card">
                <div className="drf-card-title">Configure & Run Drift Report</div>
                <form className="drf-form" onSubmit={handleSubmit}>
                    <div className="drf-form-group">
                        <label className="drf-label">Report Mode</label>
                        <div className="drf-mode-selector">
                            <button
                                type="button"
                                className={`drf-mode-btn ${mode === 'monthly' ? 'active' : ''}`}
                                onClick={() => { setMode('monthly'); setError(null); setSelectedYear(''); setSelectedMonth(''); }}
                            >📅 Monthly</button>
                            <button
                                type="button"
                                className={`drf-mode-btn ${mode === 'custom' ? 'active' : ''}`}
                                onClick={() => { setMode('custom'); setError(null); }}
                            >🗓️ Custom Range</button>
                        </div>
                    </div>

                    {mode === 'monthly' && (
                        <div className="drf-date-row">
                            <div className="drf-form-group">
                                <label className="drf-label">Year <span className="drf-required">*</span></label>
                                {monthsLoading ? (
                                    <div className="drf-table-loading">
                                        <span className="drf-btn-spinner drf-spinner-dark"></span> Loading…
                                    </div>
                                ) : (
                                    <select
                                        className="drf-select"
                                        value={selectedYear}
                                        onChange={e => { setSelectedYear(e.target.value); setSelectedMonth(''); }}
                                        required
                                    >
                                        <option value="">Select year</option>
                                        {(availableMonths?.years || []).map(y => (
                                            <option key={y.year} value={y.year}>{y.year}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="drf-form-group">
                                <label className="drf-label">Month <span className="drf-required">*</span></label>
                                <select
                                    className="drf-select"
                                    value={selectedMonth}
                                    onChange={e => setSelectedMonth(e.target.value)}
                                    disabled={!selectedYear}
                                    required
                                >
                                    <option value="">Select month</option>
                                    {selectedYear &&
                                        (availableMonths?.years.find(y => y.year === Number(selectedYear))?.months || []).map(m => (
                                            <option key={m} value={m}>{MONTH_NAMES[m - 1]}</option>
                                        ))
                                    }
                                </select>
                            </div>
                        </div>
                    )}

                    {mode === 'custom' && (
                        <div className="drf-date-row">
                            <div className="drf-form-group">
                                <label className="drf-label">Start Date <span className="drf-required">*</span></label>
                                <input
                                    type="date"
                                    className="drf-input"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="drf-form-group">
                                <label className="drf-label">End Date <span className="drf-required">*</span></label>
                                <input
                                    type="date"
                                    className="drf-input"
                                    value={endDate}
                                    min={startDate || undefined}
                                    onChange={e => setEndDate(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                    )}

                    <div className="drf-actions">
                        <button type="submit" className="drf-submit-btn" disabled={loading}>
                            {loading
                                ? <><span className="drf-btn-spinner"></span>Submitting…</>
                                : '🚀 Run Drift Report'}
                        </button>
                        {(jobStatus || error) && (
                            <button type="button" className="drf-reset-btn" onClick={handleReset}>
                                🔄 Reset
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {error && (
                <div className="drf-status-card drf-status-error">
                    <div className="drf-status-header">
                        <span className="drf-status-icon">❌</span>
                        <div>
                            <h3 className="drf-status-title">Failed to Submit</h3>
                            <p className="drf-status-sub">Please check the inputs and try again.</p>
                        </div>
                    </div>
                    <div className="drf-error-body">
                        <p className="drf-error-message">{error}</p>
                    </div>
                </div>
            )}

            {renderStatusBanner()}

            <HistoryTable token={token} refreshTick={refreshTick} onBrowse={uid => setBrowseUid(uid)} />

            {browseUid && (
                <DriftFileExplorer uid={browseUid} token={token} onClose={() => setBrowseUid(null)} />
            )}
        </div>
    );
};

export default DriftReportManagement;
